import { Localized } from '../../i18n/I18n';
import React, { useRef, useState } from 'react';
import axios from 'axios';
import { ArrowUpRight, Check, LoaderCircle, Mail, MessageCircle, Phone } from 'lucide-react';
import { contactData } from '../../data/mock';
import { serviceCatalog } from '../../data/serviceCatalog';
import './ProjectContact.css';
import { LegalLink } from '../privacy/PrivacyCenter';
import { POLICY_VERSION } from '../privacy/consentStorage';

const initialValues = {
  name: '', email: '', countryCode: '+57', phone: '',
  interest: 'Integraciones y automatización con IA', message: '', website: '', consent: false,
};

const countryCodes = [
  ['+57', 'Colombia'], ['+1', 'EE. UU. / Canadá'], ['+52', 'México'], ['+34', 'España'],
  ['+54', 'Argentina'], ['+56', 'Chile'], ['+51', 'Perú'], ['+593', 'Ecuador'],
  ['+507', 'Panamá'], ['+506', 'Costa Rica'], ['+598', 'Uruguay'], ['+55', 'Brasil'],
  ['+44', 'Reino Unido'], ['+49', 'Alemania'], ['+33', 'Francia'],
];

function ProjectContact() {
  const [values, setValues] = useState(initialValues);
  const [status, setStatus] = useState({ type: '', message: '' });
  const [sending, setSending] = useState(false);
  const pending = useRef(false);

  const updateValue = ({ target }) => {
    setValues((previous) => ({ ...previous, [target.name]: target.type === 'checkbox' ? target.checked : target.value }));
    if (status.type === 'success') setStatus({ type: '', message: '' });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (pending.current || !event.currentTarget.reportValidity()) return;
    const phone = `${values.countryCode}${values.phone.replace(/\D/g, '')}`;
    if (values.name.trim().length < 2 || values.message.trim().length < 10 || !values.consent) {
      setStatus({ type: 'error', message: 'Revisa tu nombre, cuéntanos un poco más de tu proyecto y acepta la política de privacidad.' });
      return;
    }
    if (!/^[\d\s()-]+$/.test(values.phone) || !/^\+\d{7,15}$/.test(phone)) {
      setStatus({ type: 'error', message: 'Revisa tu teléfono: escribe el número sin el indicativo, que ya seleccionaste a la izquierda.' });
      return;
    }
    pending.current = true;
    setSending(true);
    setStatus({ type: '', message: 'Enviando tu mensaje…' });

    const data = new FormData();
    data.append('name', values.name.trim());
    data.append('email', values.email.trim());
    data.append('phone', phone);
    data.append('message', `Interés: ${values.interest}\n\n${values.message.trim()}`);
    data.append('website', values.website);
    data.append('consent', '1');
    data.append('consent_version', POLICY_VERSION);

    try {
      const response = await axios.post('/out.php', data, { timeout: 15000 });
      if (response.data?.success !== true) {
        setStatus({ type: 'error', message: 'No pudimos confirmar el envío. Conservamos tus datos para que puedas intentarlo de nuevo o escribirnos por WhatsApp.' });
        return;
      }
      setStatus({ type: 'success', message: '¡Mensaje recibido! Nuestro equipo se pondrá en contacto contigo para conocer tu proyecto.' });
      setValues({ ...initialValues });
    } catch (error) {
      const message = error.code === 'ECONNABORTED'
        ? 'El servidor tardó más de lo esperado y no pudimos confirmar el envío. Puedes intentarlo de nuevo o escribirnos por WhatsApp.'
        : error.response?.status === 429
          ? 'Hemos recibido varios intentos seguidos. Espera unos minutos para volver a enviar o escríbenos por WhatsApp.'
          : error.response?.status === 400 || error.response?.status === 422
            ? 'No se pudo validar el formulario. Revisa los datos y vuelve a intentarlo; también puedes escribirnos por WhatsApp.'
            : 'No pudimos confirmar el envío. Revisa tu conexión e inténtalo de nuevo, o escríbenos por WhatsApp. Tus datos siguen aquí.';
      setStatus({ type: 'error', message });
    } finally {
      pending.current = false;
      setSending(false);
    }
  };

  return (
    <Localized><section id="contacto" className="bz-contact" aria-labelledby="bz-contact-title">
      <div className="bz-contact__layout">
        <div className="bz-contact__intro">
          <span className="bz-contact__eyebrow"><span aria-hidden="true" /> HABLEMOS DEL RETO</span>
          <h2 id="bz-contact-title">¿Qué debería funcionar <span>mejor?</span></h2>
          <p className="bz-contact__description">Cuéntanos el proceso que quieres conectar, automatizar o simplificar.</p>
          <div className="bz-contact__next">
            <span className="bz-contact__next-icon"><Check size={18} aria-hidden="true" /></span>
            <p><strong>Primero entendemos.</strong><br />Después trazamos una ruta.</p>
          </div>
          <div className="bz-contact__direct">
            <a className="bz-contact__whatsapp" href={`https://wa.me/${contactData.whatsapp.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer">
              <MessageCircle size={20} aria-hidden="true" /><span>Prefiero hablar por WhatsApp</span><ArrowUpRight size={18} aria-hidden="true" />
            </a>
            <a href={`mailto:${contactData.email}`}><Mail size={17} aria-hidden="true" />{contactData.email}</a>
            <a href={`tel:${contactData.phone.replace(/[^+\d]/g, '')}`}><Phone size={17} aria-hidden="true" />{contactData.phone}</a>
          </div>
        </div>

        <form className="bz-contact__form" onSubmit={handleSubmit} aria-busy={sending} data-testid="project-contact-form">
          <div className="bz-contact__form-heading"><span>Cuéntanos el reto</span><ArrowUpRight size={22} aria-hidden="true" /></div>
          <p className="bz-contact__required">Te respondemos con una primera ruta.</p>
          <fieldset className="bz-contact__fields" disabled={sending}>
            <div className="bz-contact__row">
              <div className="bz-contact__field"><label htmlFor="bz-contact-name">Tu nombre</label><input id="bz-contact-name" name="name" autoComplete="name" placeholder="Nombre y apellido" value={values.name} onChange={updateValue} minLength={2} maxLength={120} required /></div>
              <div className="bz-contact__field"><label htmlFor="bz-contact-email">Correo electrónico</label><input id="bz-contact-email" name="email" type="email" autoComplete="email" placeholder="tu@empresa.com" value={values.email} onChange={updateValue} maxLength={254} required /></div>
            </div>
            <div className="bz-contact__phone-row">
              <div className="bz-contact__field"><label htmlFor="bz-contact-country">Indicativo</label><select id="bz-contact-country" name="countryCode" autoComplete="tel-country-code" value={values.countryCode} onChange={updateValue}>{countryCodes.map(([code, country]) => <option value={code} key={code}>{country} ({code})</option>)}</select></div>
              <div className="bz-contact__field"><label htmlFor="bz-contact-phone">Teléfono</label><input id="bz-contact-phone" name="phone" type="tel" inputMode="tel" autoComplete="tel-national" placeholder="300 123 4567" value={values.phone} onChange={updateValue} maxLength={25} required /></div>
            </div>
            <div className="bz-contact__field"><label htmlFor="bz-contact-interest">¿En qué podemos ayudarte?</label><select id="bz-contact-interest" name="interest" value={values.interest} onChange={updateValue} required><option>Integraciones y automatización con IA</option>{serviceCatalog.map(service => <option key={service.slug}>{service.title}</option>)}<option>Necesito orientación</option></select></div>
            <div className="bz-contact__field"><label htmlFor="bz-contact-message">Cuéntanos tu idea o tu reto</label><textarea id="bz-contact-message" name="message" placeholder="Queremos conectar nuestras herramientas, automatizar un proceso…" rows={4} value={values.message} onChange={updateValue} minLength={10} maxLength={4900} required /></div>
            <div className="bz-contact__honeypot" aria-hidden="true"><label htmlFor="bz-contact-website">Sitio web</label><input id="bz-contact-website" name="website" autoComplete="off" tabIndex={-1} value={values.website} onChange={updateValue} /></div>
            <div className="bz-contact__consent"><input id="bz-contact-consent" name="consent" type="checkbox" checked={values.consent} onChange={updateValue} required /><span><label htmlFor="bz-contact-consent">Autorizo el tratamiento de mis datos para atender esta solicitud.</label> <LegalLink tab="data" authorize={() => setValues(previous => ({ ...previous, consent: true }))}>Leer política y autorización</LegalLink></span></div>
            <button className="bz-contact__submit" type="submit" disabled={sending || !values.consent}>{sending ? <><span>Enviando mensaje</span><LoaderCircle className="bz-contact__spinner" size={19} aria-hidden="true" /></> : <><span>Conversemos sobre tu proyecto</span><ArrowUpRight size={20} aria-hidden="true" /></>}</button>
          </fieldset>
          <p className={`bz-contact__status ${status.type ? `bz-contact__status--${status.type}` : ''}`} role="status" aria-live="polite" aria-atomic="true">{status.message}</p>
        </form>
      </div>
    </section></Localized>
  );
}

export default ProjectContact;
